/**
 * useInboundForeignKeyRows Hook
 *
 * Fetches rows from a referencing (child) table for a given parent row and inbound FK definition.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createExecutionId } from '../../../../types/queryExecution';
import { fetchTableExplorerRows } from '../services/tableExplorerApi';
import type { TableExplorerInboundForeignKey } from '../../../../types/tableExplorer';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import type { LooseRelationship } from '../../../../types/electron';
import { buildInboundForeignKeyFilters, buildInboundForeignKeyFiltersKey } from '../utils/inboundForeignKeyUtils';

type UseInboundForeignKeyRowsParams = {
  connectionId: string;
  database: string;
  foreignKey: TableExplorerInboundForeignKey;
  parentRow: Record<string, unknown> | null;
  enabled: boolean;
  limit?: number;
  looseRel?: LooseRelationship;
};

type InboundForeignKeyRowsResult = {
  rows: Record<string, unknown>[];
  columns: TableExplorerColumn[];
  totalRowCount?: number;
};

export function useInboundForeignKeyRows(params: UseInboundForeignKeyRowsParams) {
  const { connectionId, database, foreignKey, parentRow, enabled, limit = 100, looseRel } = params;

  const filterBuild = useMemo(() => {
    if (!parentRow) return { filters: null, missingColumns: [] as string[] };
    return buildInboundForeignKeyFilters({ foreignKey, parentRow, looseRel });
  }, [foreignKey, parentRow, looseRel]);

  const filtersKey = useMemo(() => {
    if (!parentRow) return null;
    return buildInboundForeignKeyFiltersKey({ foreignKey, parentRow });
  }, [foreignKey, parentRow]);

  const isQueryEnabled = enabled && !!connectionId && !!foreignKey.referencingTable && !!parentRow && !!filterBuild.filters;

  const query = useQuery<InboundForeignKeyRowsResult>({
    queryKey: [
      'table-inbound-fk-rows',
      connectionId,
      database,
      foreignKey.constraintName,
      foreignKey.referencingSchema,
      foreignKey.referencingTable,
      filtersKey,
      limit,
    ],
    queryFn: async () => {
      if (!filterBuild.filters) {
        return { rows: [], columns: [] };
      }

      const executionId = createExecutionId(`inbound-fk-${foreignKey.referencingTable}`);
      const result = await fetchTableExplorerRows({
        connectionId,
        executionId,
        table: {
          database,
          schema: foreignKey.referencingSchema,
          tableName: foreignKey.referencingTable,
        },
        columns: '*',
        limit,
        filters: filterBuild.filters,
        sorting: [],
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch related rows');
      }

      return {
        rows: result.rows || [],
        columns: result.columns || [],
        totalRowCount: result.totalRowCount,
      };
    },
    enabled: isQueryEnabled,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    missingColumns: filterBuild.missingColumns,
    filters: filterBuild.filters,
  };
}

